import React from 'react';
import { Box, Text } from '@mantine/core';
import { QRFrameType } from '@/utils/qrcode/types';

interface QRCodeFrameProps {
    children: React.ReactNode;
    frameType?: QRFrameType;
    frameColor?: string;
    frameText?: string;
    frameTextColor?: string;
}

export const QRCodeFrame: React.FC<QRCodeFrameProps> = ({
    children,
    frameType = 'none',
    frameColor = '#000000',
    frameText = 'SCAN ME',
    frameTextColor = '#FFFFFF'
}) => {
    if (frameType === 'none') {
        return <>{children}</>;
    }

    // Frame styles based on type
    const getFrameStyle = (): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            padding: '15px',
            border: `2px solid ${frameColor}`,
            backgroundColor: frameColor,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box'
        };

        switch (frameType) {
            case 'square':
                return {
                    ...baseStyle,
                    borderRadius: '0px',
                };
            case 'rounded':
                return {
                    ...baseStyle,
                    borderRadius: '16px',
                };
            case 'phone':
                return {
                    ...baseStyle,
                    padding: '20px 12px 30px',
                    borderRadius: '24px',
                    position: 'relative',
                };
            case 'tablet':
                return {
                    ...baseStyle,
                    padding: '10px 10px 50px', // Increase bottom padding significantly
                    borderRadius: '12px',
                    position: 'relative',
                };
            case 'circle':
                return {
                    ...baseStyle,
                    borderRadius: '50%',
                    aspectRatio: '1 / 1', // Ensure perfect circle
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    paddingBottom: frameText ? '40px' : '12px', // Add extra padding at the bottom for text
                };
            case 'clipboard':
                return {
                    ...baseStyle,
                    padding: '25px 12px 12px',
                    borderRadius: '8px 8px 4px 4px',
                    position: 'relative',
                };
            default:
                return baseStyle;
        }
    };

    // QR code container styles - white background to ensure scannability
    const qrContainerStyle: React.CSSProperties = {
        backgroundColor: '',
        padding: '4px', // Reduced padding to minimize white space
        borderRadius: frameType === 'circle' ? '50%' : frameType === 'square' ? '0px' : '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: frameType === 'circle' ? '90%' : '100%',
        height: frameType === 'circle' ? '90%' : '100%',
        minWidth: '100px',
        minHeight: '100px',
        boxSizing: 'border-box',
        overflow: 'visible' // Ensure QR code is not clipped
    };

    // Text label styles
    const textStyle: React.CSSProperties = {
        color: frameTextColor,
        fontWeight: 'bold',
        marginTop: '8px',
        fontSize: frameText && frameText.length > 10 ? '12px' : frameText && frameText.length > 7 ? '13px' : '14px',
        textAlign: 'center',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    };

    // Additional elements for specific frames
    const renderFrameElements = () => {
        if (frameType === 'phone') {
            return (
                <Box
                    style={{
                        position: 'absolute',
                        top: '8px',
                        width: '60px',
                        height: '6px',
                        backgroundColor: '#333',
                        borderRadius: '3px',
                    }}
                />
            );
        }

        if (frameType === 'tablet') {
            return (
                <Box
                    style={{
                        position: 'absolute',
                        bottom: '35px', // Position higher above the text
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '20%', // Wider horizontal button
                        height: '4px', // Very thin
                        backgroundColor: '#333',
                        borderRadius: '2px',
                    }}
                />
            );
        }

        if (frameType === 'clipboard') {
            return (
                <Box
                    style={{
                        position: 'absolute',
                        top: '-10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '30%', // Use percentage instead of fixed width
                        height: '20px',
                        backgroundColor: frameColor,
                        borderRadius: '10px 10px 0 0',
                        border: `2px solid ${frameColor}`,
                        borderBottom: 'none',
                    }}
                />
            );
        }

        return null;
    };

    return (
        <Box style={getFrameStyle()}>
            {renderFrameElements()}
            <Box style={qrContainerStyle}>
                {children}
            </Box>
            {frameText && frameType !== 'tablet' && frameType !== 'circle' && <Text title={frameText} style={textStyle}>{frameText}</Text>}
            {frameType === 'tablet' && frameText && (
                <Text 
                    title={frameText} 
                    style={{
                        ...textStyle,
                        position: 'absolute',
                        bottom: '10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%'
                    }}
                >
                    {frameText}
                </Text>
            )}
            {frameType === 'circle' && frameText && (
                <Text 
                    title={frameText} 
                    style={{
                        ...textStyle,
                        position: 'absolute',
                        bottom: '10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold'
                    }}
                >
                    {frameText}
                </Text>
            )}
        </Box>
    );
};